import {
  isCloudLinkedItem,
  type Item,
} from "@/pages/workspace/utils/workspaceItemUtils";
import type { CloudRenameFailure, CloudRenameResult } from "./graphRename";

export type RenameDocumentDeps = {
  /** Renames the document's OneDrive/SharePoint copy. Never expected to throw. */
  renameInCloud: (item: Item, name: string) => Promise<CloudRenameResult>;
  /** Renames the document in the workspace. Throws on refusal or failure. */
  renameInWorkspace: (name: string) => Promise<unknown>;
};

export type RenameDocumentOutcome =
  | { ok: true; renamedInCloud: boolean }
  | {
      ok: false;
      stage: "cloud";
      reason: CloudRenameFailure;
      status?: number;
      graphErrorCode?: string;
    }
  | {
      ok: false;
      stage: "workspace";
      error: unknown;
      /**
       * `none`: a local file, the cloud was never involved. `reverted`: the cloud
       * copy carried the new name for a moment and is back on the old one, so
       * nothing has changed anywhere. `stillRenamed`: the cloud copy could not be
       * put back and now disagrees with the workspace.
       */
      cloud: "none" | "reverted" | "stillRenamed";
    };

/** The workspace refused or failed; nothing is renamed anywhere. */
export const WORKSPACE_RENAME_FAILED_MESSAGE =
  "Kunne ikke omdøbe filen. Tjek at der ikke allerede findes en fil med samme navn.";

/**
 * The one state this flow cannot fully undo. Sync is no help here — it applies the
 * same one-name-per-folder rule the workspace just refused with — but renaming again
 * is: the cloud copy already carries the name, so only the workspace write is redone.
 */
export const CLOUD_STILL_RENAMED_MESSAGE =
  "Filen er omdøbt i OneDrive/SharePoint, men ikke i workspacet. Prøv at omdøbe filen igen.";

/**
 * Renames a document everywhere its name lives, in the one order that fails safely.
 *
 * For a cloud-linked file the OneDrive/SharePoint copy is the source of truth — sync
 * mirrors its name back over the workspace's — so it is renamed there first. If the
 * cloud refuses, nothing has changed anywhere. If the workspace then refuses (the
 * one-name-per-folder rule is checked against a possibly stale client) or fails, the
 * cloud rename is undone again, because the alternative — a cloud copy on the new
 * name and a workspace on the old — cannot be repaired by sync, which refuses the
 * same clash. Only a failed undo leaves the two apart, and the caller says so.
 */
export const runDocumentRename = async (
  item: Item,
  newName: string,
  deps: RenameDocumentDeps
): Promise<RenameDocumentOutcome> => {
  const inCloud = isCloudLinkedItem(item);

  if (inCloud) {
    const cloud = await deps.renameInCloud(item, newName);
    if (!cloud.ok) {
      return {
        ok: false,
        stage: "cloud",
        reason: cloud.reason,
        status: cloud.status,
        graphErrorCode: cloud.graphErrorCode,
      };
    }
  }

  try {
    await deps.renameInWorkspace(newName);
    return { ok: true, renamedInCloud: inCloud };
  } catch (error) {
    if (!inCloud)
      return { ok: false, stage: "workspace", error, cloud: "none" };

    const reverted = await revertCloudRename(item, deps);
    return {
      ok: false,
      stage: "workspace",
      error,
      cloud: reverted ? "reverted" : "stillRenamed",
    };
  }
};

const revertCloudRename = async (
  item: Item,
  deps: RenameDocumentDeps
): Promise<boolean> => {
  try {
    return (await deps.renameInCloud(item, item.name)).ok;
  } catch {
    return false;
  }
};
